import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveX, Play, RefreshCw, Save } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, type Session, type StorageGcItem, type StorageGcRun, type StorageGcSettings } from "../api";
import { EmptyState, ErrorNotice, LoadingRows, Status, formatTime } from "../components";

const PROVIDERS: Record<string, string> = {
  tencent_cos: "腾讯云 COS",
  aliyun_oss: "阿里云 OSS",
  huawei_obs: "华为云 OBS",
  volcengine_tos: "火山云 TOS",
  s3_compatible: "自定义 S3 / MinIO",
};

export function StorageGcPage({ role }: { role: Session["role"] }) {
  const queryClient = useQueryClient();
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const settings = useQuery({
    queryKey: ["storage-gc-settings"],
    queryFn: () => api<StorageGcSettings>("/admin/v1/storage-gc/settings"),
    refetchInterval: 10_000,
  });
  const runs = useQuery({
    queryKey: ["storage-gc-runs"],
    queryFn: () => api<StorageGcRun[]>("/admin/v1/storage-gc/runs?limit=100"),
    refetchInterval: 5_000,
  });
  const items = useQuery({
    queryKey: ["storage-gc-items", selectedRun],
    queryFn: () => api<StorageGcItem[]>(`/admin/v1/storage-gc/runs/${selectedRun}/items`),
    enabled: Boolean(selectedRun),
    refetchInterval: selectedRun ? 5_000 : false,
  });
  const save = useMutation({
    mutationFn: (body: unknown) => api<StorageGcSettings>("/admin/v1/storage-gc/settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
    onSuccess: (value) => {
      queryClient.setQueryData(["storage-gc-settings"], value);
      setFeedback("调度规则已保存，下一轮会按新周期执行。");
    },
  });
  const runNow = useMutation({
    mutationFn: () => api<StorageGcRun>("/admin/v1/storage-gc/runs", {
      method: "POST",
      body: JSON.stringify({ reason: "从控制台立即执行到期清理" }),
    }),
    onSuccess: async (run) => {
      setSelectedRun(run.id);
      setFeedback("清理任务已进入队列，将在数秒内开始执行。");
      await queryClient.invalidateQueries({ queryKey: ["storage-gc-runs"] });
    },
  });
  const current = settings.data;
  const canOperate = role !== "VIEWER";
  const canConfigure = role === "ADMIN";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!current) return;
    setFeedback(null);
    const form = new FormData(event.currentTarget);
    save.mutate({
      enabled: form.get("enabled") === "on",
      interval_seconds: Number(form.get("interval_minutes")) * 60,
      expected_version: current.version,
      reason: "更新到期清理调度规则",
    });
  }

  const activeRun = runs.data?.find((run) => run.id === selectedRun) ?? null;
  return <div className="page page--storage-gc">
    <header className="page-heading storage-gc-heading">
      <div><p className="eyebrow">STORAGE / LIFECYCLE</p><h1>到期清理</h1><p>按保留期限回收云存储对象，并保留每次调度和每个对象的删除记录。</p></div>
      <button className="button button--primary" disabled={!canOperate || runNow.isPending} onClick={() => runNow.mutate()}><Play size={16} />{runNow.isPending ? "正在提交…" : "立即执行"}</button>
    </header>

    {(settings.error || runs.error || save.error || runNow.error) && <ErrorNotice error={settings.error ?? runs.error ?? save.error ?? runNow.error} />}
    {feedback && <div className="notice notice--positive">{feedback}</div>}
    <div className="notice notice--neutral"><ArchiveX size={17} /><span>腾讯云 COS、阿里云 OSS、华为云 OBS、火山云 TOS 会执行真实删除；自定义 S3 / MinIO 只记录“需外部清理”，服务不会主动删除。</span></div>

    {current && <section className="storage-gc-runtime">
      <div className="storage-gc-runtime__state"><span className="eyebrow">调度状态</span><strong>{current.enabled ? "自动运行" : "已暂停"}</strong><Status value={current.enabled ? "ACTIVE" : "DISABLED"} /></div>
      <dl><div><dt>下次执行</dt><dd>{current.enabled ? formatTime(current.next_run_at) : "—"}</dd></div><div><dt>最近开始</dt><dd>{formatTime(current.last_started_at)}</dd></div><div><dt>最近完成</dt><dd>{formatTime(current.last_finished_at)}</dd></div></dl>
    </section>}

    <section className="storage-gc-settings section-block section-block--first">
      <div className="section-heading"><div><p className="eyebrow">SCHEDULE</p><h2>调度规则</h2></div><span className="section-heading__meta">控制面单实例执行，不会重复触发</span></div>
      {settings.isPending ? <LoadingRows columns={3} /> : current && <form key={current.version} onSubmit={submit}>
        <label className="check storage-gc-enable"><input name="enabled" type="checkbox" defaultChecked={current.enabled} disabled={!canConfigure} /><span><strong>启用自动清理</strong><small>暂停后仍可人工立即执行</small></span></label>
        <label className="field">执行周期（分钟）<input name="interval_minutes" type="number" min="1" max="10080" defaultValue={Math.max(1, Math.round(current.interval_seconds / 60))} disabled={!canConfigure} /><span className="field__hint">每轮处理开始时已到期的全部记录，失败记录留待下一轮重试。</span></label>
        <button className="button" type="submit" disabled={!canConfigure || save.isPending}><Save size={16} />{save.isPending ? "保存中…" : "保存规则"}</button>
      </form>}
    </section>

    <section className="section-block storage-gc-history">
      <div className="section-heading"><div><p className="eyebrow">RUN HISTORY</p><h2>删除任务</h2></div><button className="button button--quiet button--small" onClick={() => void runs.refetch()} disabled={runs.isFetching}><RefreshCw size={15} className={runs.isFetching ? "spin" : ""} />刷新</button></div>
      <div className={`storage-gc-workspace ${selectedRun ? "storage-gc-workspace--detail" : ""}`}>
        <div>{runs.isPending ? <LoadingRows columns={6} /> : runs.data?.length ? <div className="table-wrap"><table className="responsive-table storage-gc-table"><thead><tr><th>触发</th><th>状态</th><th>处理记录</th><th>删除对象</th><th>失败 / 外部</th><th>完成时间</th></tr></thead><tbody>{runs.data.map((run) => <tr key={run.id} className={selectedRun === run.id ? "table-row--selected" : ""} onClick={() => setSelectedRun(run.id)}><td data-label="触发"><strong>{run.trigger_type === "MANUAL" ? "人工执行" : "定时执行"}</strong><small>{formatTime(run.created_at)}</small></td><td data-label="状态"><Status value={run.status} /></td><td data-label="处理记录"><strong>{run.claimed_records}</strong></td><td data-label="删除对象"><strong>{run.deleted_objects}</strong><small>{formatBytes(run.deleted_bytes)}</small></td><td data-label="失败 / 外部"><strong>{run.failed_objects} / {run.external_objects}</strong><small>失败 / 需外部清理</small></td><td data-label="完成时间">{formatTime(run.finished_at)}</td></tr>)}</tbody></table></div> : <EmptyState title="还没有删除记录">启用定时调度或点击“立即执行”后，每一轮都会在这里留下汇总。</EmptyState>}</div>
        {selectedRun && <aside className="storage-gc-detail"><header><div><p className="eyebrow">RUN DETAIL</p><h3>本轮对象明细</h3></div><button className="icon-button" aria-label="关闭详情" onClick={() => setSelectedRun(null)}>×</button></header>{activeRun && <div className="storage-gc-detail__summary"><Status value={activeRun.status} /><span>{activeRun.deleted_objects} 个已删除</span><span>{activeRun.failed_objects} 个失败</span></div>}{items.isPending ? <LoadingRows columns={2} /> : items.error ? <ErrorNotice error={items.error} /> : items.data?.length ? <div className="storage-gc-items">{items.data.map((item) => <article key={item.id}><header><Status value={item.status} /><time>{formatTime(item.finished_at ?? item.started_at)}</time></header><strong>{PROVIDERS[item.provider] ?? item.provider}</strong><code title={item.object_key}>{item.object_key}</code><small>{item.backend_id} · {formatBytes(item.size_bytes)} · {item.owner_kind === "RESULT" ? "成品" : "失败 Attempt"}</small>{item.error_message && <p>{item.error_message}</p>}</article>)}</div> : <EmptyState title="本轮没有到期对象">调度正常完成，但当前没有需要处理的数据。</EmptyState>}</aside>}
      </div>
    </section>
  </div>;
}

function formatBytes(value: number | null) {
  if (value == null) return "大小未知";
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KiB`;
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} MiB`;
  return `${(value / 1024 ** 3).toFixed(2)} GiB`;
}
